/**
 * <UserflowsCatalogPage> - Iter 9.40 Pass F
 *
 * Admin "every flow at a glance" - replaces the old MAIN_USER_FLOWS.md viewer
 * with a screenshot-driven catalog where every screen shows:
 *   • Live screenshot
 *   • Description
 *   • Dynamic copy keys it pulls (with current values)
 *   • Inline "Edit copy" buttons that write straight to db.me_template /
 *     db.platform_settings
 *   • "Open in editor" deep-link to the matching admin section
 *
 * Backend: GET /api/admin/userflows-catalog, PUT /copy/{key}, PUT (full).
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Image as ImageIcon, Pencil, ExternalLink, RefreshCw, Loader2,
  ChevronDown, ChevronRight, Map as MapIcon, User, Shield, Briefcase, Globe,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const ACTOR_META = {
  client:        { label: "Client",        icon: User,      color: "bg-purple-50 text-purple-700" },
  host:          { label: "Host",          icon: Briefcase, color: "bg-rose-50 text-rose-700" },
  admin:         { label: "Admin",         icon: Shield,    color: "bg-gray-100 text-gray-800" },
  guest:         { label: "Guest",         icon: Globe,     color: "bg-emerald-50 text-emerald-700" },
  brand_partner: { label: "Brand partner", icon: Globe,     color: "bg-amber-50 text-amber-700" },
};

const CopyEditor = ({ flowId, copyKey, currentValue, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(currentValue || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/userflows-catalog/copy/${encodeURIComponent(copyKey)}`, {
        method: "PUT",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ value: val }),
      });
      if (res.ok) {
        toast.success(`Saved ${copyKey}`);
        setOpen(false);
        onSaved?.(copyKey, val);
      } else {
        toast.error("Save failed");
      }
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-left flex items-start justify-between gap-2 px-2 py-1.5 rounded hover:bg-gray-100 transition-colors w-full group"
        data-testid={`userflow-copy-${flowId}-${copyKey}`}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono text-gray-500">{copyKey}</div>
          <div className="text-xs text-gray-800 truncate">
            {currentValue || <span className="italic text-gray-400">- not set -</span>}
          </div>
        </div>
        <Pencil className="w-3 h-3 text-gray-400 group-hover:text-gray-700 mt-0.5 flex-shrink-0" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`userflow-copy-modal-${copyKey}`}>
          <DialogHeader>
            <DialogTitle className="text-base">Edit copy</DialogTitle>
            <p className="text-[10px] font-mono text-gray-500">{copyKey}</p>
          </DialogHeader>
          <Textarea
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="Enter copy…"
            className="min-h-[100px]"
            data-testid={`userflow-copy-input-${copyKey}`}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid={`userflow-copy-save-${copyKey}`}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const FlowCard = ({ flow, expanded, onToggle, onCopySaved }) => {
  const ActorIcon = (ACTOR_META[flow.actor] || ACTOR_META.client).icon;
  const actorColor = (ACTOR_META[flow.actor] || ACTOR_META.client).color;

  return (
    <Card className="overflow-hidden" data-testid={`userflow-${flow.id}`}>
      <CardContent className="p-0">
        <button
          onClick={onToggle}
          className="w-full text-left p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors"
          data-testid={`userflow-toggle-${flow.id}`}
        >
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          <div className={`w-7 h-7 rounded-md flex items-center justify-center ${actorColor}`}>
            <ActorIcon className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">{flow.name}</div>
            <div className="text-xs text-gray-500 truncate">{flow.description}</div>
          </div>
          <Badge variant="outline" className="text-[10px] capitalize">{flow.actor}</Badge>
        </button>

        {expanded && (
          <div className="border-t bg-gray-50/40">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
              {/* Screenshot */}
              <div className="bg-white rounded-lg border overflow-hidden">
                {flow.screenshot_url ? (
                  <img
                    src={flow.screenshot_url}
                    alt={flow.name}
                    className="w-full h-auto"
                    data-testid={`userflow-screenshot-${flow.id}`}
                  />
                ) : (
                  <div className="aspect-video flex items-center justify-center bg-gray-100">
                    <ImageIcon className="w-6 h-6 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Editable copy keys */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase text-gray-500">Editable copy</h4>
                {(flow.editable_copy_keys || []).length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">No copy keys exposed for this flow.</p>
                ) : (
                  <div className="bg-white rounded-lg border divide-y">
                    {flow.editable_copy_keys.map((k) => (
                      <CopyEditor
                        key={k}
                        flowId={flow.id}
                        copyKey={k}
                        currentValue={flow.current_copy?.[k]}
                        onSaved={(key, val) => onCopySaved?.(flow.id, key, val)}
                      />
                    ))}
                  </div>
                )}
                {flow.edit_link && (
                  <a
                    href={flow.edit_link}
                    className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline pt-1"
                    data-testid={`userflow-edit-link-${flow.id}`}
                  >
                    Open in admin editor <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function UserflowsCatalogPage() {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState("all");

  const fetchCatalog = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/userflows-catalog`, { headers: auth() });
      if (res.ok) {
        const data = await res.json();
        setFlows(data.flows || []);
        // Auto-expand the first one
        if ((data.flows || []).length > 0) {
          setExpanded({ [data.flows[0].id]: true });
        }
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchCatalog(); }, []);

  const onCopySaved = (flowId, key, val) => {
    setFlows((prev) => prev.map((f) =>
      f.id === flowId ? { ...f, current_copy: { ...(f.current_copy || {}), [key]: val } } : f
    ));
  };

  const filteredFlows = filter === "all" ? flows : flows.filter((f) => f.actor === filter);
  const actors = Array.from(new Set(flows.map((f) => f.actor))).filter(Boolean);

  return (
    <div className="space-y-5" data-testid="userflows-catalog-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <MapIcon className="w-5 h-5" /> Userflows
          </h2>
          <p className="text-sm text-gray-500">
            Every screen, modal & popup with the dynamic copy that drives them. Edit any text inline - it syncs to /me Template + host templates instantly.
          </p>
        </div>
        <button
          onClick={fetchCatalog}
          className="p-2 hover:bg-gray-100 rounded-lg"
          data-testid="userflows-refresh"
        >
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Actor filter */}
      <div className="flex gap-2 flex-wrap" data-testid="userflows-filter">
        <button
          onClick={() => setFilter("all")}
          className={`px-3 py-1 rounded-full text-xs font-medium ${filter === "all" ? "bg-black text-white" : "bg-gray-100 text-gray-700"}`}
          data-testid="userflows-filter-all"
        >
          All ({flows.length})
        </button>
        {actors.map((a) => {
          const meta = ACTOR_META[a] || {};
          const count = flows.filter((f) => f.actor === a).length;
          return (
            <button
              key={a}
              onClick={() => setFilter(a)}
              className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${filter === a ? "bg-black text-white" : "bg-gray-100 text-gray-700"}`}
              data-testid={`userflows-filter-${a}`}
            >
              {meta.label || a} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-10" data-testid="userflows-loading">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="space-y-2.5">
          {filteredFlows.map((f) => (
            <FlowCard
              key={f.id}
              flow={f}
              expanded={!!expanded[f.id]}
              onToggle={() => setExpanded((p) => ({ ...p, [f.id]: !p[f.id] }))}
              onCopySaved={onCopySaved}
            />
          ))}
        </div>
      )}
    </div>
  );
}
